"use client";

import { useState, useEffect, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { phoneNumberPricingService, type PhoneNumberPricingInfo, type AssignPhoneNumberRequest } from '@/app/_module/services/phoneNumberPricingService';
import { useToast } from '@/app/_module/hooks/use-toast';

// Query keys for phone number data
export const phoneNumberQueryKeys = {
  all: ['phoneNumbers'] as const,
  pricing: () => [...phoneNumberQueryKeys.all, 'pricing'] as const,
  userNumbers: () => [...phoneNumberQueryKeys.all, 'userNumbers'] as const,
};

/**
 * Hook to get user's phone number pricing information
 */
export function usePhoneNumberPricing() {
  return useQuery({
    queryKey: phoneNumberQueryKeys.pricing(),
    queryFn: () => phoneNumberPricingService.getUserPricingInfo(),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });
}

/**
 * Hook to get user's current phone numbers
 */
export function useUserPhoneNumbers() {
  return useQuery({
    queryKey: phoneNumberQueryKeys.userNumbers(),
    queryFn: () => phoneNumberPricingService.getUserPhoneNumbers(),
    staleTime: 30_000, // 30 seconds
    gcTime: 10 * 60 * 1000, // 10 minutes
    refetchInterval: 30_000, // Refetch every 30 seconds for live updates
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });
}

/**
 * Hook to assign a new phone number with pricing confirmation
 */
export function useAssignPhoneNumber() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [isConfirming, setIsConfirming] = useState(false);

  const mutation = useMutation({
    mutationFn: async (request: AssignPhoneNumberRequest & { skipConfirmation?: boolean }) => {
      // Get current pricing info
      const pricingInfo = await phoneNumberPricingService.getUserPricingInfo();
      
      // Check if user can add more numbers
      if (!pricingInfo.canAddMore) {
        throw new Error('You have reached your phone number limit');
      }

      // Get confirmation message
      const confirmationMessage = phoneNumberPricingService.getAddPhoneConfirmationMessage(pricingInfo);
      
      // Show confirmation if needed and not skipped
      if (confirmationMessage && !request.skipConfirmation) {
        setIsConfirming(true);
        const confirmed = window.confirm(confirmationMessage);
        setIsConfirming(false);
        
        if (!confirmed) {
          throw new Error('User cancelled phone number assignment');
        }
      }

      // Assign the phone number
      return phoneNumberPricingService.assignPhoneNumber({
        areaCode: request.areaCode,
        state: request.state,
      });
    },
    onSuccess: async (data) => {
      
      // Aggressively invalidate and refetch all phone number related queries
      queryClient.invalidateQueries({ queryKey: phoneNumberQueryKeys.pricing() });
      queryClient.invalidateQueries({ queryKey: phoneNumberQueryKeys.userNumbers() });
      queryClient.invalidateQueries({ queryKey: phoneNumberQueryKeys.all });
      
      // Also invalidate any cached available phone numbers
      queryClient.invalidateQueries({ queryKey: ['availablePhoneNumbers'] });
      
      // Add a small delay to allow backend to process the assignment
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Force refetch the user phone numbers immediately
      await queryClient.refetchQueries({ queryKey: phoneNumberQueryKeys.userNumbers() });
      
      // Also refetch pricing info in case it changed
      await queryClient.refetchQueries({ queryKey: phoneNumberQueryKeys.pricing() });
      
      toast({
        title: "Phone Number Assigned",
        description: `Successfully assigned ${data.data.phoneNumber.number}`,
      });
    },
    onError: (error) => {
      if (error.message !== 'User cancelled phone number assignment') {

        let errorMessage = "Failed to assign phone number";
        
        if (error.message) {
          if (error.message.includes('Invalid area code format')) {
            errorMessage = "Invalid area code format. Please select a valid phone number.";
          } else if (error.message.includes('Invalid phone number format')) {
            errorMessage = "Invalid phone number format. Please try again.";
          } else if (error.message.includes('No phone number or area code provided')) {
            errorMessage = "Missing phone number data. Please try again.";
          } else {
            errorMessage = error.message;
          }
        }
        
        toast({
          title: "Error",
          description: errorMessage,
          variant: "destructive",
        });
      }
    },
  });

  return {
    assignPhoneNumber: mutation.mutate,
    isAssigning: mutation.isPending,
    isConfirming,
    error: mutation.error,
    reset: mutation.reset,
  };
}

/**
 * Hook that combines pricing info and assignment functionality
 */
export function usePhoneNumberManager() {
  const pricingQuery = usePhoneNumberPricing();
  const phoneNumbersQuery = useUserPhoneNumbers();
  const assignMutation = useAssignPhoneNumber();

  const getPricingDisplay = useCallback((pricingInfo?: PhoneNumberPricingInfo) => {
    if (!pricingInfo) return '';
    return phoneNumberPricingService.formatPricingDisplay(pricingInfo);
  }, []);

  const canAddPhoneNumber = pricingQuery.data?.canAddMore ?? false;
  const isUnlimited = pricingQuery.data?.freePhoneNumbers === 999999;
  const hasFreeSlotsRemaining = (pricingQuery.data?.totalPhoneNumbers ?? 0) < (pricingQuery.data?.freePhoneNumbers ?? 0);

  return {
    // Pricing information
    pricingInfo: pricingQuery.data,
    isPricingLoading: pricingQuery.isLoading,
    pricingError: pricingQuery.error,
    
    // Phone numbers
    phoneNumbers: phoneNumbersQuery.data || [],
    isPhoneNumbersLoading: phoneNumbersQuery.isLoading,
    phoneNumbersError: phoneNumbersQuery.error,
    
    // Assignment functionality
    assignPhoneNumber: assignMutation.assignPhoneNumber,
    isAssigning: assignMutation.isAssigning,
    isConfirming: assignMutation.isConfirming,
    assignmentError: assignMutation.error,
    
    // Utility functions and states
    getPricingDisplay,
    canAddPhoneNumber,
    isUnlimited,
    hasFreeSlotsRemaining,
    
    // Refresh functions
    refreshPricing: pricingQuery.refetch,
    refreshPhoneNumbers: phoneNumbersQuery.refetch,
  };
}